import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckoutButton } from "./CheckoutButton";
import { CountUp } from "./CountUp";
import { useSite } from "@/context/SiteContent";

const PER_PAGE = 6;
const FILTERS = ["All", "Purpose", "People", "Program", "Place", "Profit"];

export const LessonExplorer = () => {
  const { lessons } = useSite();
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [current, setCurrent] = useState(0);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const h = (e) => {
      const i = lessons.findIndex((l) => l.title === e.detail?.title);
      if (i < 0) return;
      setFilter("All"); setQuery(""); setCurrent(i); setPage(Math.floor(i / PER_PAGE));
      setFlash(true); setTimeout(() => setFlash(false), 1400);
    };
    window.addEventListener("c2c:lesson", h);
    return () => window.removeEventListener("c2c:lesson", h);
  }, [lessons]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    let arr = lessons.map((data, index) => ({ data, index }));
    if (filter !== "All") arr = arr.filter((x) => x.data.category === filter);
    if (q) arr = arr.filter((x) => `${x.data.category} ${x.data.title} ${x.data.text}`.toLowerCase().includes(q));
    return arr;
  }, [filter, query, lessons]);

  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, pages - 1);
  const pageItems = filtered.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);
  const visibleIndex = filtered.some((x) => x.index === current) ? current : (filtered[0]?.index ?? current);
  const d = lessons[visibleIndex];

  const step = (dir) => {
    if (!filtered.length) return;
    const pos = filtered.findIndex((x) => x.index === visibleIndex);
    setCurrent(filtered[(pos + dir + filtered.length) % filtered.length].index);
  };
  const surprise = () => {
    if (!filtered.length) return;
    const pos = Math.floor(Math.random() * filtered.length);
    setCurrent(filtered[pos].index);
    setPage(Math.floor(pos / PER_PAGE));
  };
  const changeFilter = (f) => { setFilter(f); setPage(0); };

  return (
    <section className="library-section lesson-explorer-section" data-rail="52 Lessons" id="library" data-testid="lessons-section">
      <div className="lesson-orb lesson-orb-one" />
      <div className="lesson-orb lesson-orb-two" />
      <div className="container lesson-explorer-wrap">
        <div className="lesson-explorer-head reveal">
          <div>
            <div className="eyebrow">Built from Scott's Campus2Corp content plan</div>
            <h2>Explore one lesson at a time.</h2>
            <p className="lead">Instead of scrolling through a wall of topics, choose a 5P, search for something on your mind, or let the explorer surface a lesson for you.</p>
          </div>
          <div className="lesson-library-stat">
            <span className="lesson-stat-number"><CountUp to={lessons.length} testId="lesson-count" /></span>
            <span><strong>lesson themes</strong><small>mapped across the 5Ps</small></span>
          </div>
        </div>
        <div className="lesson-control-bar reveal">
          <div className="lesson-filter-row">
            {FILTERS.map((f) => <button key={f} className={`filter-btn${filter === f ? " active" : ""}`} onClick={() => changeFilter(f)} data-testid={`lesson-filter-${f.toLowerCase()}`}>{f}</button>)}
          </div>
          <div className="lesson-actions">
            <label className="searchbox lesson-search"><span>⌕</span>
              <input placeholder="Search a topic" type="search" value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} data-testid="lesson-search-input" />
            </label>
            <button className="lesson-surprise" type="button" onClick={surprise} data-testid="lesson-surprise-btn">Surprise me ✦</button>
          </div>
        </div>
        <div className="lesson-explorer-grid reveal">
          <article className={`lesson-spotlight${flash ? " is-flash" : ""}`} data-testid="lesson-spotlight">
            <div className="lesson-spotlight-top">
              <div>
                <span className="lesson-category" data-testid="spotlight-category">{d.category.toUpperCase()}</span>
                <span className="lesson-position">Lesson {visibleIndex + 1} of {lessons.length}</span>
              </div>
              <span className="lesson-spotlight-mark">C2C</span>
            </div>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={visibleIndex} className="lesson-spotlight-body" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.32, ease: [0.2, 0.7, 0.2, 1] }}>
                <div className="lesson-topic-label">Currently exploring</div>
                <h3 data-testid="spotlight-title">{d.title}</h3>
                <p>{d.text}</p>
                <div className="lesson-reflection"><span>Think about this</span><p>{d.question}</p></div>
              </motion.div>
            </AnimatePresence>
            <div className="lesson-spotlight-footer">
              <div className="lesson-nav-buttons">
                <button aria-label="Previous lesson" type="button" onClick={() => step(-1)} data-testid="lesson-prev-btn">←</button>
                <button aria-label="Next lesson" type="button" onClick={() => step(1)} data-testid="lesson-next-btn">→</button>
              </div>
              <CheckoutButton className="lesson-ebook-link" testId="lesson-checkout-link">Explore all 52 lessons in the eBook →</CheckoutButton>
            </div>
          </article>
          <div className="lesson-browser">
            <div className="lesson-browser-head">
              <div>
                <span className="lesson-browser-kicker">Choose a lesson</span>
                <strong data-testid="lesson-browser-title">{!filtered.length ? "No lessons found" : filter === "All" ? "A curated selection from all 5Ps" : `${filter} lessons`}</strong>
              </div>
              <span className="lesson-page-count" data-testid="lesson-page-count">{filtered.length ? `${safePage + 1} / ${pages}` : "0 / 0"}</span>
            </div>
            <div className="lesson-mini-list">
              {pageItems.map((item, pos) => (
                <button key={item.index} className={`lesson-mini${item.index === visibleIndex ? " active" : ""}`} onClick={() => setCurrent(item.index)} data-testid={`lesson-mini-${item.index}`}>
                  <span className="lesson-mini-index">{String(safePage * PER_PAGE + pos + 1).padStart(2, "0")}</span>
                  <span className="lesson-mini-copy"><small>{item.data.category.toUpperCase()}</small><strong>{item.data.title}</strong></span>
                  <span className="lesson-mini-arrow">→</span>
                </button>
              ))}
            </div>
            <div className="lesson-browser-footer">
              <button type="button" onClick={() => setPage((safePage - 1 + pages) % pages)} data-testid="lesson-prev-page">← Previous</button>
              <div className="lesson-dots">{Array.from({ length: filtered.length ? pages : 0 }, (_, i) => <span key={i} className={`lesson-dot${i === safePage ? " active" : ""}`} />)}</div>
              <button type="button" onClick={() => setPage((safePage + 1) % pages)} data-testid="lesson-next-page">Next →</button>
            </div>
            <div className="library-empty" style={{ display: filtered.length ? "none" : "block" }} data-testid="lesson-empty">No matching lesson found. Try another keyword.</div>
          </div>
        </div>
        <div className="lesson-explorer-note reveal">
          <span>Why this is here</span>
          <p>Campus2Corp is designed to be useful in moments. You do not need every lesson today. Find the one that matches the situation you are dealing with now, then come back when the next transition arrives.</p>
        </div>
      </div>
    </section>
  );
};
